import { MY_GROUPS_QUERY_KEY } from '@api/endpoints/groupPlay'
import { ReactNode, useState, useEffect, useRef, createContext, useContext } from 'react'
import { SUPPORT_QUERY_KEYS } from '@api/endpoints/support'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Notifications,
  AccountCircle,
  Search,
  Menu as MenuIcon,
  Settings,
  Logout,
  FilterAlt,
  ConfirmationNumber,
  Chat,
  CreditCard,
  Fullscreen,
  FullscreenExit,
  DarkMode,
  LightMode,
  SettingsBrightness,
  WbTwilight,
  QrCode2,
  AdminPanelSettings,
  Circle,
  Schedule,
  DoNotDisturbOn,
  VisibilityOff,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@store/authStore'
import { useTimeZoneReporting } from '@hooks/useTimeZoneReporting'
import { useUIStore } from '@store/uiStore'
import { Sidebar, SIDEBAR_WIDTH_FULL, SIDEBAR_WIDTH_ICONS, type SidebarCollapseMode } from '@components/navigation/Sidebar'
import { getUnreadCount } from '@api/endpoints/notices'
import { NoticeTypeIds } from '@api/types/notices'
import { chatApi } from '@api/endpoints/chat'
import { useNotificationConnection, useGeneralChatConnection } from '@hooks/useSignalR'
import { useFirebaseBootstrap } from '@hooks/useFirebaseBootstrap'
import { usePresenceHeartbeat } from '@hooks/usePresenceHeartbeat'
import { presenceApi } from '@api/endpoints/presence'
import { PresenceState, presenceColor } from '@api/types/presence'
import { settingsApi } from '@api/endpoints/settings'
import { useFirestoreNotifications } from '@hooks/useFirestoreNotifications'
import { useFirestoreConversations } from '@hooks/useFirestoreConversations'
import { useNotificationPreferencesStore } from '@store/notificationPreferencesStore'
import { useSilentAdventureStore } from '@store/silentAdventureStore'
import { playNotificationSound } from '@utils/notificationSound'
import NotificationToast from '@components/common/NotificationToast'
import { startLocationTracking, stopLocationTracking } from '@services/locationTracking'
import { useChatStore } from '@store/chatStore'
import { ChatPopoutManager } from '@features/chat/components/ChatPopoutManager'
import { ProfileCompletionBanner } from '@components/common/ProfileCompletionBanner'
import { StaffMfaBanner } from '@components/common/StaffMfaBanner'
import { resolveNotificationMode } from '@api/types/chat'
import ShareProfileQrDialog from '@features/social/components/ShareProfileQrDialog'
import NotificationsDropdown from '@features/notifications/components/NotificationsDropdown'
import { moderatorApprovalsApi } from '@api/endpoints/moderatorApprovals'
import { useSettingsSyncInvalidation } from '@hooks/useSettingsSync'

const SIDEBAR_COLLAPSE_KEY = 'sidebarCollapseMode'

// Build version timestamp - format: yyyyMMddHHmm (injected at build time by Vite)
declare const __BUILD_VERSION__: string
const BUILD_VERSION = __BUILD_VERSION__

// Context for sidebar width
interface SidebarContextType {
  sidebarWidth: number
}

const SidebarContext = createContext<SidebarContextType>({ sidebarWidth: SIDEBAR_WIDTH_FULL })

export const useSidebarWidth = () => useContext(SidebarContext)

interface MainLayoutProps {
  children: ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const { isAuthenticated, user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  useTimeZoneReporting()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [modToast, setModToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [infoToast, setInfoToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [inviteToast, setInviteToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [silentToast, setSilentToast] = useState<{ open: boolean; message: string; adventureId: string | null }>({ open: false, message: '', adventureId: null })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) // <900px

  // Settings changed on another device. Mounted here, once, so anything that merely *reads* a
  // setting stays honest without knowing this feature exists — the presence dot in the account
  // menu below, units on a distance, whether the leaderboard thinks you are on it. Screens that
  // hold a form take the change differently and opt in themselves; see useSettingsSection.
  useSettingsSyncInvalidation()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // <600px

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapseMode, setSidebarCollapseMode] = useState<SidebarCollapseMode>(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSE_KEY)
    return (saved ? parseInt(saved, 10) : 2) as SidebarCollapseMode
  })

  const handleSidebarCollapseModeChange = (mode: SidebarCollapseMode) => {
    setSidebarCollapseMode(mode)
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, mode.toString())

    // Dispatch resize events during and after transition to trigger map resize
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 250)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  // Get current sidebar width based on collapse mode
  const currentSidebarWidth = sidebarCollapseMode === 0 ? 0 : sidebarCollapseMode === 1 ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Presence: tell the server this tab is alive and collect presence for whatever is on screen.
  // One request per beat serves the whole app — the watch list comes from the presence store, not
  // from whoever happens to be rendering. Deliberately not mounted from useFirebaseBootstrap:
  // presence left Firestore precisely because its audience cannot be decided client-side.
  usePresenceHeartbeat(isAuthenticated)

  // Firebase realtime bootstrap (sign-in + web push + foreground handler). No-op unless configured;
  // runs in parallel with SignalR during the migration cutover.
  useFirebaseBootstrap()

  // Global conversation listener — sidebar previews + unread badges, and active-conversation refetch
  // on read/reaction/participant changes. Replaces the SignalR ConversationUpdateSignal fan-out.
  useFirestoreConversations(user?.userId)

  // Keep the SignalR notification hub connected — live session voting (useSessionSignalR) still
  // rides it. General notifications (the bell) now arrive via Firestore instead of this hub.
  useNotificationConnection()

  // Realtime notifications via Firestore (replaces the SignalR bell path).
  useFirestoreNotifications(user?.userId, {
    onNotice: (notice) => {
      // Refresh notification-related queries (the notice list itself still loads from REST)
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['moderatorApprovals'] })
      // A group play invite, reminder or Story party change: My Adventures' Groups section.
      queryClient.invalidateQueries({ queryKey: MY_GROUPS_QUERY_KEY })
      // Also refresh conversations — notifications include being added to new conversations
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      // A moderator answering a ticket or deciding a suggestion arrives as a notice — refresh the
      // Support badge and lists rather than waiting for the poll.
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.summary })
      queryClient.invalidateQueries({ queryKey: ['contributions'] })

      const noticeType = (notice.type || '').toUpperCase()

      // Show toast for moderator approval requests
      if (noticeType === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase()) {
        const msg = notice.message || 'A participant is waiting for moderator approval.'
        const prefs = useNotificationPreferencesStore.getState().moderatorApproval
        if (prefs.sound) playNotificationSound('moderator')
        if (prefs.popup) {
          setModToast({ open: true, message: notice.title ? `${notice.title} — ${msg}` : msg })
        }
      }

      // Show toast for incoming adventure / session invites. Both invite
      // types share the same per-user "adventure invite" preference category.
      const isAdventureInvite = noticeType === NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase()
      const isSessionInvite = noticeType === NoticeTypeIds.SESSION_INVITE.toUpperCase()
      if (isAdventureInvite || isSessionInvite) {
        const msg = notice.message
          || (isAdventureInvite ? 'You have a new adventure invite.' : 'You have been invited to a session.')
        const prefs = useNotificationPreferencesStore.getState().adventureInvite
        if (prefs.sound) playNotificationSound('invite')
        if (prefs.popup) {
          setInviteToast({ open: true, message: notice.title ? `${notice.title} — ${msg}` : msg })
        }
      }

      // A group forming for something you flagged, and the split offered when a vote does not
      // suit you, are both invitations to join something — same preference category, same toast.
      const isMatchAvailable = noticeType === NoticeTypeIds.SESSION_MATCH_AVAILABLE.toUpperCase()
      const isSplitProposed = noticeType === NoticeTypeIds.SESSION_SPLIT_PROPOSED.toUpperCase()
      if (isMatchAvailable || isSplitProposed) {
        const msg = notice.message
          || (isMatchAvailable
            ? 'A group you qualify for is forming.'
            : 'An alternative time is available for you.')
        const prefs = useNotificationPreferencesStore.getState().adventureInvite
        if (prefs.sound) playNotificationSound('invite')
        if (prefs.popup) {
          setInviteToast({ open: true, message: notice.title ? `${notice.title} — ${msg}` : msg })
        }
      }

      // Show toast and refresh invites for session invite responses (accepted/declined)
      const title = notice.title || ''
      if (title.toLowerCase().includes('session invite declined') || title.toLowerCase().includes('session invite accepted')) {
        const msg = notice.message || title
        setInfoToast({ open: true, message: msg })
        // Refresh session invite and participant lists
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'sessions' && (q.queryKey[1] === 'invites' || q.queryKey[1] === 'participants') })
      }
    },
    onNoticeDeleted: (noticeId) => {
      if (!noticeId) return
      // Optimistically remove the notice from the cached list
      queryClient.setQueryData<any[]>(['userNotices'], (old) => {
        if (!old) return old
        return old.filter((n: any) => n.id !== noticeId)
      })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
    },
  })

  // Initialize SignalR chat connection globally for unread badge updates
  const { isConnected: isChatConnected, onConversationUpdateSignal, onUserJoinedConversation, joinConversation: joinChatRoom, leaveConversation: leaveChatRoom } = useGeneralChatConnection()

  // Fetch conversations for initial unread counts (before room joins reset them)
  // Your own status, shared by key with the chat settings dialog so the two never disagree.
  const { data: privacySettings } = useQuery({
    queryKey: ['privacySettings'],
    queryFn: () => settingsApi.getPrivacySettings(),
    enabled: isAuthenticated,
  })
  const presenceStatus = privacySettings?.presenceStatus ?? PresenceState.Online

  const presenceMutation = useMutation({
    mutationFn: (status: PresenceState) => presenceApi.setStatus(status),
    // Write the response straight into the cache rather than invalidating: the menu is open and
    // watching, and a refetch would make the buttons flicker back before settling.
    onSuccess: (updated) => queryClient.setQueryData(['privacySettings'], updated),
  })

  const { data: globalConversations } = useQuery({
    queryKey: ['chatConversations'],
    queryFn: chatApi.getConversations,
    enabled: isAuthenticated,
  })

  // Compute unread badge directly from query data
  const unreadChatCount = globalConversations?.reduce(
    (sum: number, c: any) => sum + (c.unreadMessageCount || 0), 0
  ) || 0

  // Join all conversation rooms so we receive SignalR events
  const joinedRoomsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isChatConnected || !globalConversations) return

    const currentIds = new Set(globalConversations.map((c: any) => c.id))

    currentIds.forEach((id) => {
      if (!joinedRoomsRef.current.has(id)) {
        joinChatRoom(id)
        joinedRoomsRef.current.add(id)
      }
    })

    joinedRoomsRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        leaveChatRoom(id)
        joinedRoomsRef.current.delete(id)
      }
    })
  }, [isChatConnected, globalConversations, joinChatRoom, leaveChatRoom])

  // Invalidate conversations query when real-time chat messages arrive
  useEffect(() => {
    if (!isChatConnected) return

    const unsub = onConversationUpdateSignal?.((signal) => {
      const isOwnMessage = signal.senderId === user?.id || signal.senderId === user?.userId
      if (signal.updateType === 'ParticipantLeft') {
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
        return
      }
      if (signal.updateType === 'NewMessage') {
        // Always refresh conversations — this handles both existing and brand-new conversations
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })

        // Nothing more to do for own messages
        if (isOwnMessage) return

        // Auto-open popout if the conversation's notification mode is AutoOpen
        const chatState = useChatStore.getState()
        // Skip if user is on the chat page (use window.location to avoid stale closure)
        if (window.location.pathname.startsWith('/chat')) {
          return
        }
        // Read conversations from the query cache at signal time to avoid stale closure
        const cachedConversations = queryClient.getQueryData<any[]>(['chatConversations'])
        const conversation = cachedConversations?.find((c: any) => c.id === signal.conversationId)
        if (conversation) {
          const mode = resolveNotificationMode(conversation.notificationMode)
          console.log('[MainLayout] Auto-open check:', { conversationId: signal.conversationId, mode, notificationMode: conversation.notificationMode })
          if (mode === 'AutoOpen') {
            chatState.addPopout(signal.conversationId)
          }
        } else {
          console.log('[MainLayout] New conversation not yet in cache, will appear after refresh:', signal.conversationId)
        }
      }
    })

    return () => { unsub?.() }
  }, [isChatConnected, onConversationUpdateSignal, queryClient, user?.id, user?.userId])

  // Handle being added to a new conversation (e.g., someone created a group chat including this user)
  useEffect(() => {
    if (!isChatConnected) return

    const unsub = onUserJoinedConversation?.((event) => {
      console.log('[MainLayout] UserJoinedConversation:', event)
      // Refresh conversations so the new one appears in the list and room-join effect fires
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      // Eagerly join the room so signals for it arrive immediately
      if (event.conversationId) {
        joinChatRoom(event.conversationId)
        joinedRoomsRef.current.add(event.conversationId)
      }
    })

    return () => { unsub?.() }
  }, [isChatConnected, onUserJoinedConversation, queryClient, joinChatRoom])

  // Start location tracking when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[MainLayout] Starting location tracking for authenticated user')
      startLocationTracking()
    }

    return () => {
      stopLocationTracking()
    }
  }, [isAuthenticated])

  // Acknowledge silent adventures auto-joined while moving around the portal.
  // The location-tracking service pushes discoveries into the store from its
  // background interval; we drain the queue one at a time into a toast so the
  // acknowledgement appears no matter which page the user is on.
  const silentQueueHead = useSilentAdventureStore((s) => s.queue[0])
  const shiftSilentAdventure = useSilentAdventureStore((s) => s.shift)
  useEffect(() => {
    if (!silentQueueHead || silentToast.open) return
    const prefs = useNotificationPreferencesStore.getState().silentAdventure
    if (prefs.sound) playNotificationSound('invite')
    if (prefs.popup) {
      setSilentToast({
        open: true,
        message: `You discovered "${silentQueueHead.title}"!`,
        adventureId: silentQueueHead.adventureId,
      })
    } else {
      // Popups muted for this category — consume the item without showing it
      // so the queue keeps draining to the next discovery.
      shiftSilentAdventure()
    }
  }, [silentQueueHead, silentToast.open, shiftSilentAdventure])

  // Fetch unread notification count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  })

  const handleChatClick = () => {
    navigate('/chat')
  }

  // Clear search query when navigating away from search results page or to advanced search
  useEffect(() => {
    if (!location.pathname.startsWith('/search') || location.pathname === '/search/advanced') {
      setSearchQuery('')
    }
  }, [location.pathname])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null)
  }

  const handleSettingsClick = () => {
    handleAccountMenuClose()
    navigate('/settings')
  }

  const handleSupportClick = () => {
    handleAccountMenuClose()
    navigate('/support/tickets')
  }

  const handleSubscriptionClick = () => {
    handleAccountMenuClose()
    navigate('/subscription')
  }

  // "Share Profile (QR)" menu item — opens a dialog with a scannable QR
  // encoding the current user's id. Another user can scan it from any of
  // the app's QR scanners to fire a friend request.
  const [shareProfileOpen, setShareProfileOpen] = useState(false)
  const handleShareProfileClick = () => {
    handleAccountMenuClose()
    setShareProfileOpen(true)
  }

  // Pending moderator-approval count for the dropdown badge. Gated on the
  // menu being open so we don't poll the endpoint for non-moderators in the
  // background. The "Moderator Approvals" item is only rendered when the
  // count is > 0 so non-moderators / nothing-pending users see no clutter.
  const { data: pendingApprovalGroups } = useQuery({
    queryKey: ['moderator-approvals', 'pending'],
    queryFn: moderatorApprovalsApi.getPending,
    enabled: Boolean(accountMenuAnchor) && isAuthenticated,
    staleTime: 30_000,
  })
  const pendingApprovalCount = (pendingApprovalGroups ?? []).reduce((sum, g) => sum + g.requests.length, 0)
  const handleModeratorApprovalsClick = () => {
    handleAccountMenuClose()
    navigate('/moderator-approvals')
  }

  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement)

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  const handleFullscreenToggle = () => {
    handleAccountMenuClose()
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      document.documentElement.requestFullscreen()
    }
  }

  const { themeMode, setThemeMode } = useUIStore()

  const handleLogout = () => {
    handleAccountMenuClose()
    logout()
  }

  // Debounced search handler
  useEffect(() => {
    if (!searchQuery.trim()) {
      return
    }

    const timer = setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchDialogOpen(false)
      setSearchQuery('')
    }
  }

  const handleSearchIconClick = () => {
    if (isSmallScreen) {
      setSearchDialogOpen(true)
    } else {
      navigate('/search/advanced')
    }
  }

  // Notifications: Facebook-style dropdown on desktop, full page on mobile.
  // Mobile gets the immediate-navigate behavior because dropdowns get cramped
  // on narrow viewports.
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLElement | null>(null)
  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      navigate('/notifications')
    } else {
      setNotificationsAnchor(event.currentTarget)
    }
  }
  const handleNotificationsClose = () => setNotificationsAnchor(null)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', width: '100%' }}>
      {/* Header spans full width */}
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'primary.main',
          color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexShrink: 0,
        }}
      >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Left: Hamburger Menu + Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Hamburger menu - always visible, toggles sidebar */}
              <IconButton
                color="inherit"
                edge="start"
                onClick={isMobile ? handleDrawerToggle : () => {
                  // Cycle through modes: 2 -> 1 -> 0 -> 2
                  const nextMode = sidebarCollapseMode === 2 ? 1 : sidebarCollapseMode === 1 ? 0 : 2
                  handleSidebarCollapseModeChange(nextMode as SidebarCollapseMode)
                }}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant="h5"
                onClick={() => navigate('/')}
                role="link"
                aria-label="Hidden Adventures — go to home"
                sx={{
                  fontFamily: '"Brush Script MT", cursive',
                  color: 'inherit',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                  cursor: 'pointer',
                }}
              >
                Hidden Adventures
              </Typography>
            </Box>

            {/* Right: Search Bar + User Actions */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              {/* Search Bar - show on larger screens */}
              <TextField
                  placeholder="Search adventures, users, groups..."
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  onBlur={() => setSearchQuery('')}
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    flex: 1,
                    maxWidth: { sm: 300, md: 450, lg: 600 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.2)' : 'rgba(255, 255, 255, 0.12)',
                      borderRadius: '24px',
                      color: 'inherit',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'white',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      '&::placeholder': {
                        color: 'inherit',
                        opacity: 0.6,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'inherit', opacity: 0.6 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/search/advanced')}
                          sx={{
                            color: 'inherit',
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <FilterAlt fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              {/* User Actions */}
              {/* Search Icon - show on small screens, hide on larger screens where search bar is visible */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleSearchIconClick}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <Search />
              </IconButton>
              {/* Chat Icon */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleChatClick}
              >
                <Badge badgeContent={unreadChatCount} color="error">
                  <Chat />
                </Badge>
              </IconButton>
              {/* Notifications Icon - always visible */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleNotificationsClick}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
              <Tooltip title={user?.displayName || 'Account'}>
                <IconButton color="inherit" size="small" onClick={handleAccountMenuOpen}>
                  {user?.avatarUrl || user?.avatar ? (
                    <Avatar
                      src={user.avatarUrl || user.avatar}
                      alt={user.displayName}
                      sx={{ width: 24, height: 24 }}
                    />
                  ) : (
                    <AccountCircle />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
      </AppBar>

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        onClick={handleAccountMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 200,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleSettingsClick}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSubscriptionClick}>
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          <ListItemText>Subscription</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShareProfileClick}>
          <ListItemIcon>
            <QrCode2 fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share Profile (QR)</ListItemText>
        </MenuItem>
        {pendingApprovalCount > 0 && (
          <MenuItem onClick={handleModeratorApprovalsClick}>
            <ListItemIcon>
              <Badge badgeContent={pendingApprovalCount} color="error" overlap="circular">
                <AdminPanelSettings fontSize="small" />
              </Badge>
            </ListItemIcon>
            <ListItemText>Moderator Approvals</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleSupportClick}>
          <ListItemIcon>
            <ConfirmationNumber fontSize="small" />
          </ListItemIcon>
          <ListItemText>Support</ListItemText>
        </MenuItem>
        {/* Just the tickets this person sent, and a way to send one. Suggestions and moderation
            live in the Support hub in the side navigation. */}
        {/* Status. onClick stopPropagation is mandatory — the parent Menu closes on click, so
            without it the menu slams shut the moment you pick a status. The Theme row below does
            the same thing for the same reason. */}
        <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Circle fontSize="small" sx={{ color: presenceColor(presenceStatus) }} />
          <ListItemText primary="Status" sx={{ flex: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={presenceStatus}
            onChange={(_, val) => {
              if (val !== null) presenceMutation.mutate(val as PresenceState)
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="presence status"
          >
            <ToggleButton value={PresenceState.Online} aria-label="online">
              <Tooltip title="Online"><Circle fontSize="small" sx={{ color: 'success.main' }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value={PresenceState.Away} aria-label="away">
              <Tooltip title="Away"><Schedule fontSize="small" sx={{ color: 'warning.main' }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value={PresenceState.Busy} aria-label="busy">
              <Tooltip title="Busy"><DoNotDisturbOn fontSize="small" sx={{ color: 'error.main' }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value={PresenceState.Offline} aria-label="appear offline">
              <Tooltip title="Appear offline"><VisibilityOff fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DarkMode fontSize="small" sx={{ color: 'action.active' }} />
          <ListItemText primary="Theme" sx={{ flex: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={themeMode}
            onChange={(_, val) => {
              if (val) setThemeMode(val)
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="theme mode"
          >
            <ToggleButton value="light" aria-label="light mode">
              <Tooltip title="Light"><LightMode fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark mode">
              <Tooltip title="Dark"><DarkMode fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="system" aria-label="system mode">
              <Tooltip title="System"><SettingsBrightness fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="auto" aria-label="auto mode, follows the sun">
              <Tooltip title="Auto (follows the sun)"><WbTwilight fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <MenuItem onClick={handleFullscreenToggle}>
          <ListItemIcon>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Version {BUILD_VERSION}
          </Typography>
        </Box>
      </Menu>

      {/* Profile-completion reminder — bar directly under the top nav, visible on
          every page (sits outside the scrollable content area). Offset by the sidebar
          width on desktop so the fixed sidebar doesn't overlap its left edge; full
          width on mobile where the sidebar is a temporary overlay. */}
      <Box
        sx={{
          flexShrink: 0,
          ml: isMobile ? 0 : `${currentSidebarWidth}px`,
          transition: 'margin 0.2s ease-in-out',
        }}
      >
        <StaffMfaBanner />
        <ProfileCompletionBanner />
      </Box>

      {/* Content area with sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', width: '100%' }}>
        <Sidebar
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
          isMobile={isMobile}
          collapseMode={sidebarCollapseMode}
          onCollapseModeChange={handleSidebarCollapseModeChange}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.default',
            overflow: 'auto',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SidebarContext.Provider value={{ sidebarWidth: currentSidebarWidth }}>
            {children}
          </SidebarContext.Provider>
        </Box>
      </Box>

      {/* Chat Popouts */}
      <ChatPopoutManager />

      {/* Share Profile QR (opened from the account menu) */}
      <ShareProfileQrDialog
        open={shareProfileOpen}
        onClose={() => setShareProfileOpen(false)}
      />

      {/* Notifications dropdown (desktop only — mobile navigates straight to
          /notifications). Anchored to the bell IconButton. */}
      <NotificationsDropdown
        anchorEl={notificationsAnchor}
        onClose={handleNotificationsClose}
      />

      {/* Mobile Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search Adventures</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search adventures, users, groups..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate('/search/advanced')
                setSearchDialogOpen(false)
              }}
            >
              Advanced Search
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Moderator approval toast — sound + popup icons embedded so the
          recipient can opt out for this category from the toast itself. */}
      <NotificationToast
        open={modToast.open}
        message={modToast.message}
        category="moderatorApproval"
        severity="warning"
        autoHideDurationMs={10000}
        onClose={() => setModToast({ open: false, message: '' })}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setModToast({ open: false, message: '' })
              navigate('/moderator-approvals')
            }}
          >
            Review
          </Button>
        }
      />

      {/* Adventure / session invite toast — same per-user toggles, separate
          category so users can mute invites without losing moderator alerts. */}
      <NotificationToast
        open={inviteToast.open}
        message={inviteToast.message}
        category="adventureInvite"
        severity="info"
        autoHideDurationMs={8000}
        onClose={() => setInviteToast({ open: false, message: '' })}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setInviteToast({ open: false, message: '' })
              navigate('/notifications')
            }}
          >
            View
          </Button>
        }
      />

      {/* Silent adventure discovery toast — fired from the location-tracking
          layer so it surfaces on any page when the user enters a silent
          adventure's geofence. Separate category so discoveries can be muted
          independently of invites/moderator alerts. */}
      <NotificationToast
        open={silentToast.open}
        message={silentToast.message}
        category="silentAdventure"
        severity="success"
        autoHideDurationMs={8000}
        onClose={() => {
          setSilentToast({ open: false, message: '', adventureId: null })
          // Advance the queue so the next discovery (if any) can show.
          shiftSilentAdventure()
        }}
        action={
          silentToast.adventureId ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const id = silentToast.adventureId
                setSilentToast({ open: false, message: '', adventureId: null })
                shiftSilentAdventure()
                if (id) navigate(`/adventures/${id}`)
              }}
            >
              View
            </Button>
          ) : undefined
        }
      />

      {/* Info toast (session invite responses, etc.) — system status, no
          per-user toggle category since it's a confirmation of an action
          the user already took. */}
      <Snackbar
        open={infoToast.open}
        autoHideDuration={5000}
        onClose={() => setInfoToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setInfoToast({ open: false, message: '' })}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {infoToast.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
